import { FileInfo, PtError } from '@/types/pt_types'
import { scanModel } from '@/utils/model'
import { buildQueryStr } from '@/utils/string'

import type { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios'

/**
 * HTTP客户端
 */
export class HttpClient {
  instance: AxiosInstance

  constructor(instance: AxiosInstance) {
    this.instance = instance
  }

  /**
   * GET请求
   * @param config request configuration
   */
  public get<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'GET'
    })
  }

  /**
   * POST请求
   * @param config request configuration
   */
  public post<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'POST'
    })
  }

  /**
   * form post请求
   * @param config
   * @protected
   */
  public formPost<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      method: 'POST',
      data: buildQueryStr(config.data || {})
    })
  }

  /**
   * json post请求
   * @param config
   * @protected
   */
  public jsonPost<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/json; charset=UTF-8'
      },
      method: 'POST',
      data: config.data
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildMultiFormData(data: any, headers: AxiosRequestHeaders) {
    if (data instanceof FormData) {
      return data
    } else {
      const formData = new FormData()
      const files = {} as Record<string, FileInfo>
      scanModel(data, (name, value) => {
        if (value instanceof FileInfo) {
          files[name] = value
        } else if (value !== undefined && value !== null) {
          formData.append(name, value)
        }
      })
      // 文件加到最后面
      for (const fileKey in files) {
        const file = files[fileKey]!
        formData.append(fileKey, file.fileContent as Blob, file.fileName)
      }
      return formData
    }
  }

  /**
   * multiForm post请求
   * @param config
   * @protected
   */
  public multiFormPost<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'POST',
      transformRequest: [(data, headers) => this.buildMultiFormData(data, headers)]
    })
  }

  /**
   * PUT请求
   * @param config request configuration
   */
  public put<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'PUT'
    })
  }

  /**
   * form put请求
   * @param config
   * @protected
   */
  public formPut<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      method: 'PUT',
      data: buildQueryStr(config.data || {})
    })
  }

  /**
   * json put请求
   * @param config
   * @protected
   */
  public jsonPut<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/json; charset=UTF-8'
      },
      method: 'PUT',
      data: config.data
    })
  }

  /**
   * multiForm put请求
   * @param config
   * @protected
   */
  public multiFormPut<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'PUT',
      transformRequest: [(data, headers) => this.buildMultiFormData(data, headers)]
    })
  }

  /**
   * DELETE请求
   * @param config request configuration
   */
  public delete<R>(config: AxiosRequestConfig): Promise<R> {
    return this.request<R>({
      ...config,
      method: 'DELETE'
    })
  }

  /**
   * 向服务端发送一个请求
   * @param config request configuration
   */
  private async request<R>(config: AxiosRequestConfig): Promise<R> {
    const response = await this.instance.request<R>(config)
    if (response.status !== 200 && response.status !== 201) {
      throw new PtError(599, '响应异常')
    }
    return response.data
  }
}
